/*
 * Frame Chooser — Rahmenauswahl für Poster (Wanders & Friends)
 * <frame-chooser>: Kartengrid, Preis je Format (variant:change), Bundle-Properties
 * <frame-overlay>: Rahmen-Overlay über Teeinblues tee-printarea--artwork (Task 4)
 * Bundle-Add via variant:add (Task 5)
 *
 * Änderung:
 * - Standardmäßig ist "Ohne Zusatz" / "Ohne Rahmen" ausgewählt
 * - Klick auf Zusatzprodukt = auswählen
 * - Klick auf dasselbe Zusatzprodukt erneut = abwählen
 * - "Ohne Zusatz"-Karte kann weiterhin verwendet oder im Liquid ausgeblendet werden
 * - Teeinblue-Vorschau wird auf 130px von oben positioniert
 * - Modal-Rahmen verschwindet beim Schließen sofort
 * - Modal-Rahmen erscheint erst, wenn das Poster stabil steht
 * - Stabilitätsprüfung auf 3 Frames beschleunigt
 * - Overlay bleibt bei gewähltem Rahmen dauerhaft sichtbar und bindet sich nach Teeinblue-Re-Renders automatisch neu
 *
 * NEU (kombinierte Variante statt Bundle):
 * - Produkte mit eigener Option "Rahmen" (aktuell: Leinwand) liefern über
 *   frame-chooser.liquid config.mode = "combined": es gibt nur noch EIN
 *   Cart-Line-Item, die kombinierte Variante wird direkt ins Formular
 *   geschrieben (siehe applyCombinedSelection()). Der zweite cart/add.js-
 *   Call sowie Bundle-ID/Rollback bleiben unverändert für Produkte ohne
 *   diese Option (config.mode = "legacy", aktuell: Poster) erhalten.
 *
 * Stand: 2026-09-13
 */

(function () {
  'use strict';

  window.__framePendingBundles = window.__framePendingBundles || {};

  /* ------------------------------------------------------------------ *
   * DIAGNOSE-LOGGER (temporär)
   *
   * Standardmäßig stumm - keine Auswirkung auf normale Kund*innen.
   * Zum Debuggen in der Konsole aktivieren mit:
   *   window.__lmDebug = true
   * und danach das zu untersuchende Verhalten (z.B. Klick neben den
   * Framechooser) auslösen. Alle Zeilen sind mit "[LM-DEBUG]"
   * markiert und können in der Konsole danach gefiltert werden.
   * ------------------------------------------------------------------ */
  window.__lmDebug = window.__lmDebug || false;

  function lmLog() {
    if (!window.__lmDebug) return;

    const args = Array.prototype.slice.call(arguments);
    args.unshift('[LM-DEBUG ' + performance.now().toFixed(0) + 'ms]');
    console.log.apply(console, args);
  }

  /* ------------------------------------------------------------------ *
   * <frame-chooser>
   * ------------------------------------------------------------------ */

  class FrameChooser extends HTMLElement {
    connectedCallback() {
      if (this.__initialized) return;
      this.__initialized = true;

      const configEl = this.querySelector('[data-frame-chooser-config]');

      this.config = configEl
        ? JSON.parse(configEl.textContent)
        : { frames: {} };

      /*
       * "combined": Rahmen ist eine echte Produktoption desselben Produkts
       * (Leinwand) -> EIN Cart-Line-Item, kombinierte Variante wird direkt
       * ins Formular geschrieben.
       * "legacy": separates Rahmenprodukt (aktuell noch Poster) -> zweiter
       * cart/add.js-Call über _bundle_id/_bundle_role (unverändert).
       */
      this.mode = this.config.mode || 'legacy';

      this.currentSize = (this.dataset.currentSize || '').trim();

      /*
       * AUSWAHLSTATUS
       *
       * null = gar nichts ausgewählt
       * -1   = "Ohne Zusatz"-Karte
       * 0+   = echtes Zusatzprodukt
       */
      this.selectedIndex = -1;

      /*
       * Karten-Klick
       */
      this.addEventListener('click', (event) => {
        const card = event.target.closest('.frame-card');

        if (!card || card.disabled) return;

        const index = parseInt(card.dataset.frameIndex, 10);

        if (Number.isNaN(index)) return;

        /*
         * Gleiche Karte erneut:
         * Auswahl entfernen.
         */
        if (this.selectedIndex === index) {
          this.clearSelection();
          return;
        }

        /*
         * Neue Karte auswählen.
         */
        this.select(index);
      });

      /*
       * Poster-Variante / Größe geändert.
       */
      document.addEventListener('variant:change', (event) => {
        const formId =
          event.target &&
          event.target.getAttribute &&
          event.target.getAttribute('id');

        if (formId !== this.dataset.formId) return;

        const variant = event.detail.variant;
        const sizeIndex = parseInt(this.dataset.sizeIndex, 10);

        if (!variant || sizeIndex < 0 || !variant.options) return;

        this.currentSize = (variant.options[sizeIndex] || '').trim();

        this.refresh();
      });

      this.initArrows();
      this.refresh();

      /*
       * STANDARD:
       * Ohne Zusatz / Ohne Rahmen.
       */
      this.select(-1);
    }

    /* -------------------------------------------------------------- *
     * Pfeil-Navigation Desktop
     * -------------------------------------------------------------- */

    initArrows() {
      const row = this.querySelector('.frame-chooser__row');
      const grid = this.querySelector('.frame-chooser__grid');
      const prev = this.querySelector('[data-frame-arrow="prev"]');
      const next = this.querySelector('[data-frame-arrow="next"]');

      if (!row || !grid || !prev || !next) return;

      const update = () => {
        row.classList.toggle(
          'has-overflow',
          grid.scrollWidth > grid.clientWidth + 4
        );

        prev.classList.toggle(
          'is-edge',
          grid.scrollLeft <= 2
        );

        next.classList.toggle(
          'is-edge',
          grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 2
        );
      };

      const step = () =>
        Math.max(grid.clientWidth * 0.6, 160);

      prev.addEventListener('click', () => {
        grid.scrollBy({
          left: -step(),
          behavior: 'smooth'
        });
      });

      next.addEventListener('click', () => {
        grid.scrollBy({
          left: step(),
          behavior: 'smooth'
        });
      });

      grid.addEventListener('scroll', update, {
        passive: true
      });

      window.addEventListener('resize', update);

      update();
    }

    /* -------------------------------------------------------------- *
     * Frame / Variante
     * -------------------------------------------------------------- */

    frameFor(index) {
      return typeof index === 'number' && index >= 0
        ? this.config.frames[String(index)]
        : null;
    }

    variantForSize(index, size) {
      const frame = this.frameFor(index);

      if (!frame) return null;

      return (
        frame.variants.find(
          (variant) => variant.size.trim() === size
        ) || null
      );
    }

    currentVariant() {
      if (
        this.selectedIndex === null ||
        this.selectedIndex < 0
      ) {
        return null;
      }

      const variant = this.variantForSize(
        this.selectedIndex,
        this.currentSize
      );

      return variant && variant.available
        ? variant
        : null;
    }

    /*
     * Ohne-Rahmen-Variante (kombinierter Modus) für eine Größe.
     * Jede Rahmen-Variante trägt in "noneId" die ID der "Ohne"-Variante
     * derselben Größe. Wird beim Zurücksetzen des id-Felds benötigt, wenn
     * "Ohne Rahmen" gewählt ist (siehe applyCombinedSelection).
     */
    noneVariantIdForSize(size) {
      const frames = Object.values(
        (this.config && this.config.frames) || {}
      );

      const allNoneIds = new Set();

      for (const frame of frames) {
        for (const variant of frame.variants || []) {
          if (!variant.noneId) continue;

          allNoneIds.add(String(variant.noneId));

          if (
            String(variant.size || '').trim() ===
            String(size || '').trim()
          ) {
            return String(variant.noneId);
          }
        }
      }

      /*
       * Fallback für Produkte mit nur EINER Größe bzw. ohne Größen-Option:
       * gibt es genau eine "Ohne"-Variante, ist sie eindeutig.
       */
      return allNoneIds.size === 1
        ? Array.from(allNoneIds)[0]
        : null;
    }

    /* -------------------------------------------------------------- *
     * Größen / Preise aktualisieren
     * -------------------------------------------------------------- */

    refresh() {
      this
        .querySelectorAll(
          '.frame-card:not(.frame-card--none)'
        )
        .forEach((card) => {
          const index = parseInt(
            card.dataset.frameIndex,
            10
          );

          const variant = this.variantForSize(
            index,
            this.currentSize
          );

          const priceEl =
            card.querySelector('[data-frame-price]');

          const usable = !!(
            variant &&
            variant.available
          );

          card.disabled = !usable;

          card.classList.toggle(
            'is-unavailable',
            !usable
          );

          if (priceEl) {
            priceEl.textContent =
              variant
                ? `+ ${variant.price}`
                : '';
          }

          if (usable) {
            card.removeAttribute('title');
          } else {
            card.title =
              this.dataset.textUnavailable || '';
          }
        });

      /*
       * Gewählter Rahmen nach Größenwechsel
       * nicht mehr verfügbar.
       */
      if (
        this.selectedIndex !== null &&
        this.selectedIndex >= 0 &&
        !this.currentVariant()
      ) {
        this.clearSelection();
      }
    }

    /* -------------------------------------------------------------- *
     * Auswahl setzen
     * -------------------------------------------------------------- */

    select(index) {
      this.selectedIndex = index;

      this
        .querySelectorAll('.frame-card')
        .forEach((card) => {
          const cardIndex = parseInt(
            card.dataset.frameIndex,
            10
          );

          const active =
            cardIndex === index;

          card.classList.toggle(
            'is-selected',
            active
          );

          card.setAttribute(
            'aria-selected',
            active ? 'true' : 'false'
          );
        });

      const frame = this.frameFor(index);

      const labelInput =
        this.querySelector(
          '[data-frame-prop-label]'
        );

      const bundleInput =
        this.querySelector(
          '[data-frame-prop-bundle]'
        );

      const roleInput =
        this.querySelector(
          '[data-frame-prop-role]'
        );

      [
        labelInput,
        bundleInput,
        roleInput
      ].forEach((input) => {
        if (!input) return;

        input.disabled = !frame;
      });

      if (labelInput) {
        labelInput.value =
          frame ? frame.label : '';
      }

      if (!frame && bundleInput) {
        bundleInput.value = '';
      }

      document.dispatchEvent(
        new CustomEvent('frame:change', {
          bubbles: true,
          detail: {
            frame,
            variant: this.currentVariant()
          }
        })
      );
    }

    /* -------------------------------------------------------------- *
     * Auswahl komplett entfernen
     * -------------------------------------------------------------- */

    clearSelection() {
      this.selectedIndex = null;

      this
        .querySelectorAll('.frame-card')
        .forEach((card) => {
          card.classList.remove(
            'is-selected'
          );

          card.setAttribute(
            'aria-selected',
            'false'
          );
        });

      const labelInput =
        this.querySelector(
          '[data-frame-prop-label]'
        );

      const bundleInput =
        this.querySelector(
          '[data-frame-prop-bundle]'
        );

      const roleInput =
        this.querySelector(
          '[data-frame-prop-role]'
        );

      if (labelInput) {
        labelInput.value = '';
        labelInput.disabled = true;
      }

      if (bundleInput) {
        bundleInput.value = '';
        bundleInput.disabled = true;
      }

      if (roleInput) {
        roleInput.disabled = true;
      }

      if (
        this.__pending &&
        this.__pending.bundleId
      ) {
        delete window.__framePendingBundles[
          this.__pending.bundleId
        ];
      }

      this.__pending = null;

      document.dispatchEvent(
        new CustomEvent('frame:change', {
          bubbles: true,
          detail: {
            frame: null,
            variant: null
          }
        })
      );
    }
  }

  if (!window.customElements.get('frame-chooser')) {
    window.customElements.define(
      'frame-chooser',
      FrameChooser
    );
  }

  /* ------------------------------------------------------------------ *
   * <frame-overlay>
   * ------------------------------------------------------------------ */

  const OVERLAY_OUTSET = 0.03;

  class FrameOverlay extends HTMLElement {
    connectedCallback() {
      if (!this.__img) {
        this.__img =
          document.createElement('img');

        this.__img.alt = '';

        if (this.__imgSrc) {
          this.__img.src = this.__imgSrc;
        }

        this.appendChild(this.__img);
      }
    }

    track(artwork, fixed) {
      this.__artwork = artwork || null;
      this.__fixed = !!fixed;

      this.classList.toggle(
        'frame-overlay--fixed',
        this.__fixed
      );
    }

    setOverlay(url) {
      this.__src = url || '';

      if (
        this.__src &&
        this.__imgSrc !== this.__src
      ) {
        this.__imgSrc = this.__src;

        if (this.__img) {
          this.__img.src = this.__src;
        }
      }
    }

    hide() {
      lmLog(
        'FrameOverlay.hide()',
        'fixed=' + this.__fixed,
        new Error().stack.split('\n').slice(1, 4).join(' | ')
      );

      this.cancelScheduledHide();

      this.style.setProperty(
        'display',
        'none',
        'important'
      );
    }

    /*
     * GNADENFRIST GEGEN FLACKERN:
     *
     * Teeinblue tauscht den Artwork-Knoten bei jedem Redraw
     * (z.B. Fokuswechsel/Blur eines Formularfelds, Tippen im
     * Namensfeld) kurz komplett aus. In genau diesem winzigen
     * Moment liefert getBoundingClientRect() nichts Brauchbares,
     * wodurch der Rahmen bisher sofort aus- und kurz danach wieder
     * eingeblendet wurde ("blitzt kurz weg").
     *
     * Ein passiver Sync (aus update()) versteckt den Rahmen deshalb
     * nicht mehr sofort, sondern erst, wenn er für eine kurze Weile
     * (220ms) tatsächlich ohne gültiges Artwork bleibt. Kommt vorher
     * ein neuer, gültiger update()-Aufruf (z.B. sobald Teeinblue den
     * neuen Knoten eingefügt hat), wird das geplante Verstecken
     * abgebrochen und der Rahmen bleibt durchgehend sichtbar.
     *
     * Bewusste/sofortige hide()-Aufrufe (z.B. beim Schließen der
     * Vorschau oder während "Neu positionieren") sind davon NICHT
     * betroffen - die rufen weiterhin direkt hide() auf.
     */
    scheduleHide() {
      lmLog(
        'FrameOverlay.scheduleHide()',
        'fixed=' + this.__fixed,
        'bereits geplant=' + !!this.__hideTimer
      );

      if (this.__hideTimer) return;

      this.__hideTimer =
        window.setTimeout(() => {
          this.__hideTimer = null;
          this.hide();
        }, 220);
    }

    cancelScheduledHide() {
      if (this.__hideTimer) {
        lmLog(
          'FrameOverlay.cancelScheduledHide()',
          'fixed=' + this.__fixed,
          '(geplantes hide() wurde abgebrochen)'
        );

        window.clearTimeout(this.__hideTimer);
        this.__hideTimer = null;
      }
    }

    update() {
      const artwork = this.__artwork;

      const rect =
        artwork && artwork.isConnected
          ? artwork.getBoundingClientRect()
          : null;

      if (
        !this.__src ||
        !rect ||
        rect.width < 20
      ) {
        lmLog(
          'FrameOverlay.update() -> ungültig, scheduleHide',
          'fixed=' + this.__fixed,
          'hatSrc=' + !!this.__src,
          'hatArtwork=' + !!artwork,
          'artworkConnected=' + (artwork ? artwork.isConnected : null),
          'rectWidth=' + (rect ? Math.round(rect.width) : null)
        );

        this.scheduleHide();
        return;
      }

      this.cancelScheduledHide();

      let left = rect.left;
      let top = rect.top;

      if (!this.__fixed) {
        const host =
          this.offsetParent ||
          this.parentElement;

        if (!host) {
          lmLog(
            'FrameOverlay.update() -> KEIN host (offsetParent UND parentElement null) -> hide()',
            'fixed=' + this.__fixed
          );

          this.hide();
          return;
        }

        const hostRect =
          host.getBoundingClientRect();

        left -= hostRect.left;
        top -= hostRect.top;
      }

      if (
        window.__lmDebug &&
        (Math.abs((this.__lastLeft || 0) - left) > 15 ||
          Math.abs((this.__lastTop || 0) - top) > 15)
      ) {
        lmLog(
          'FrameOverlay.update() -> Position springt deutlich',
          'fixed=' + this.__fixed,
          'von=(' + Math.round(this.__lastLeft || 0) + ',' + Math.round(this.__lastTop || 0) + ')',
          'zu=(' + Math.round(left) + ',' + Math.round(top) + ')'
        );
      }

      this.__lastLeft = left;
      this.__lastTop = top;

      const outset =
        rect.width * OVERLAY_OUTSET;

      /*
       * SCROLL-FIX V4 – SUBPIXEL-STABILISIERUNG:
       * Browser/Teeinblue liefern beim Scrollen teilweise Werte wie
       * 412.3333 / 412.6667 px. Das Artwork wird beim Painten aber auf das
       * physische Pixelraster gelegt. Wenn der Rahmen die Rohwerte übernimmt,
       * kann er optisch um einen Bruchteil bis ca. 1 CSS-Pixel hoch/runter
       * springen. Deshalb rasten wir Position UND Größe auf das echte
       * Device-Pixel-Raster ein.
       */
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const snapToDevicePixel = (value) =>
        Math.round(value * dpr) / dpr;

      const snappedLeft = snapToDevicePixel(left - outset);
      const snappedTop = snapToDevicePixel(top - outset);
      const snappedWidth = snapToDevicePixel(rect.width + 2 * outset);
      const snappedHeight = snapToDevicePixel(rect.height + 2 * outset);

      /*
       * Position nicht mehr über wechselnde left/top-Layoutwerte bewegen.
       * Ein fixed Overlay wird ab (0,0) per GPU-Transform exakt auf das
       * aktuelle Viewport-Rechteck des Teeinblue-Artworks geschoben.
       * Das reduziert vertikales Zittern bei Sticky-/Scroll-Transforms.
       */
      if (this.__fixed) {
        this.style.left = '0px';
        this.style.top = '0px';
        this.style.transform =
          `translate3d(${snappedLeft}px, ${snappedTop}px, 0)`;
        this.style.willChange = 'transform, width, height';
      } else {
        this.style.transform = '';
        this.style.willChange = '';
        this.style.left =
          `${left - outset}px`;
        this.style.top =
          `${top - outset}px`;
      }

      this.style.width =
        `${snappedWidth}px`;

      this.style.height =
        `${snappedHeight}px`;

      this.style.setProperty(
        'display',
        'block',
        'important'
      );
    }
  }

  if (!window.customElements.get('frame-overlay')) {
    window.customElements.define(
      'frame-overlay',
      FrameOverlay
    );
  }

  /* ------------------------------------------------------------------ *
   * MOBILE WHITESPACE OBERHALB DER TEEINBLUE-VORSCHAU
   *
   * Wichtig:
   * - Kein separates Spacer-Geschwister mehr.
   *   Der alte Spacer konnte durch den Gap des Parent-Layouts
   *   deutlich größer wirken als der eingestellte Pixelwert.
   *
   * - Stattdessen bekommt die komplette product-gallery nur dann
   *   einen sehr dünnen weißen oberen Rand, wenn tatsächlich eine
   *   Teeinblue-Live-Vorschau vorhanden ist.
   *
   * - Normale Shopify-Produktmedien bleiben komplett unverändert.
   * - Artwork und Frame-Overlay bewegen sich gemeinsam.
   * ------------------------------------------------------------------ */

  const FRAME_MOBILE_MEDIA =
    window.matchMedia('(max-width: 749px)');

  const MOBILE_PREVIEW_SPACE =
    12;

  const MOBILE_PREVIEW_SPACER_CLASS =
    'lm-tee-mobile-preview-space';


  function syncMobilePreviewSpacing() {
    /*
     * Alte Spacer aus vorherigen Versionen immer entfernen.
     */
    document
      .querySelectorAll(
        '.' + MOBILE_PREVIEW_SPACER_CLASS
      )
      .forEach((spacer) => {
        spacer.remove();
      });


    const galleries =
      Array.from(
        document.querySelectorAll(
          'product-gallery.product-gallery'
        )
      );


    galleries.forEach((gallery) => {
      /*
       * Modal-/Dialog-Galerien nicht verändern.
       */
      if (gallery.closest('.vm--container')) {
        return;
      }


      /*
       * Abstand nur bei echter Teeinblue-Live-Vorschau.
       */
      const hasTeeinblueLivePreview =
        !!gallery.querySelector(
          'div.tee-printarea--artwork'
        );


      if (
        FRAME_MOBILE_MEDIA.matches &&
        hasTeeinblueLivePreview
      ) {
        gallery.style.setProperty(
          'border-top',
          `${MOBILE_PREVIEW_SPACE}px solid #ffffff`,
          'important'
        );

        gallery.style.setProperty(
          'box-sizing',
          'border-box',
          'important'
        );
      } else {
        gallery.style.removeProperty(
          'border-top'
        );

        gallery.style.removeProperty(
          'box-sizing'
        );
      }
    });
  }


  function handleFrameMobileMediaChange() {
    syncMobilePreviewSpacing();
    scheduleOverlaySync();
  }


  if (
    typeof FRAME_MOBILE_MEDIA.addEventListener ===
    'function'
  ) {
    FRAME_MOBILE_MEDIA.addEventListener(
      'change',
      handleFrameMobileMediaChange
    );
  } else if (
    typeof FRAME_MOBILE_MEDIA.addListener ===
    'function'
  ) {
    FRAME_MOBILE_MEDIA.addListener(
      handleFrameMobileMediaChange
    );
  }



  /* ------------------------------------------------------------------ *
   * Overlay-Verwaltung
   * ------------------------------------------------------------------ */

  const overlayState = {
    src: '',
    gallery: null,
    modal: null,

    modalClosing: false,

    modalRepositioning: false,

    modalWaitingForStable: false,

    modalWasPresent: false,

    modalStableWatcherRunning: false,

    modalGeneration: 0,

    // Zeitpunkt, ab dem der Rahmen im Teeinblue-Modal eingeblendet werden darf.
    // Dadurch kann sich die Vorschau erst setzen und der Rahmen wackelt nicht mit.
    modalRevealAt: 0,

    galleryRepositioning: false,

    galleryWaitingForStable: false,

    galleryStableWatcherRunning: false,

    galleryGeneration: 0
  };

  /* ------------------------------------------------------------------ *
   * TEEINBLUE-VORSCHAU POSITION
   * ------------------------------------------------------------------ */

  const TEE_PREVIEW_TOP = 130;

  function positionTeeinbluePreview() {
    const modal =
      document.querySelector(
        '.tee-dialog-product-mockup .vm--modal[aria-expanded="true"]'
      );

    if (!modal) return;

    modal.style.setProperty(
      'top',
      `${TEE_PREVIEW_TOP}px`,
      'important'
    );
  }
    /* ------------------------------------------------------------------ *
   * MODAL ARTWORK FINDEN
   * ------------------------------------------------------------------ */

  function getModalArtwork() {
    const artworks =
      Array.from(
        document.querySelectorAll(
          'div.tee-printarea--artwork'
        )
      );

    return (
      artworks.find(
        (el) =>
          el.closest('.vm--container')
      ) ||
      null
    );
  }

  /* ------------------------------------------------------------------ *
   * MODAL-RAHMEN UNSICHTBAR HALTEN
   * ------------------------------------------------------------------ */

  function hideModalFrame() {
    const overlay =
      overlayState.modal;

    if (!overlay) return;

    overlay.track(
      null,
      true
    );

    overlay.hide();
  }

  /*
   * Rahmen der normalen Produktvorschau sofort verstecken.
   */
  function hideGalleryFrame() {
    const overlay =
      overlayState.gallery;

    if (!overlay) return;

    lmLog(
      'hideGalleryFrame() aufgerufen (SOFORT, keine Gnadenfrist)',
      new Error().stack.split('\n').slice(1, 4).join(' | ')
    );

    overlay.track(
      null,
      false
    );

    overlay.hide();
  }

  /*
   * Aktuelles Teeinblue-Artwork außerhalb des Modals.
   */
  function getGalleryArtwork() {
    const artworks =
      Array.from(
        document.querySelectorAll(
          'div.tee-printarea--artwork'
        )
      );

    return (
      artworks.find(
        (el) =>
          !el.closest(
            '.vm--container'
          )
      ) ||
      null
    );
  }

  /*
   * Nach "Abbrechen" oder "Speichern" rendert Teeinblue
   * die normale Produktvorschau noch kurz neu.
   *
   * Deshalb Rahmen erst wieder zeigen, wenn das Artwork
   * mehrere Frames hintereinander stabil steht.
   */
  function startGalleryStabilityWatcher() {
    if (
      overlayState.galleryStableWatcherRunning
    ) {
      return;
    }

    overlayState.galleryStableWatcherRunning =
      true;

    const generation =
      overlayState.galleryGeneration;

    let lastArtwork =
      null;

    let lastRect =
      null;

    let stableFrames =
      0;

    const REQUIRED_STABLE_FRAMES =
      5;

    const TOLERANCE =
      0.5;

    function almostSame(a, b) {
      return (
        Math.abs(a - b) <=
        TOLERANCE
      );
    }

    function check() {
      if (
        generation !==
        overlayState.galleryGeneration
      ) {
        overlayState.galleryStableWatcherRunning =
          false;

        return;
      }

      if (
        overlayState.galleryRepositioning
      ) {
        overlayState.galleryStableWatcherRunning =
          false;

        return;
      }

      if (
        !overlayState.galleryWaitingForStable
      ) {
        overlayState.galleryStableWatcherRunning =
          false;

        return;
      }

      const artwork =
        getGalleryArtwork();

      if (
        !artwork ||
        !artwork.isConnected
      ) {
        lastArtwork =
          null;

        lastRect =
          null;

        stableFrames =
          0;

        requestAnimationFrame(
          check
        );

        return;
      }

      const rect =
        artwork.getBoundingClientRect();

      if (
        rect.width < 20 ||
        rect.height < 20
      ) {
        lastArtwork =
          artwork;

        lastRect =
          null;

        stableFrames =
          0;

        requestAnimationFrame(
          check
        );

        return;
      }

      /*
       * Teeinblue kann den kompletten Artwork-Knoten ersetzen.
       */
      if (
        artwork !== lastArtwork
      ) {
        lastArtwork =
          artwork;

        lastRect = {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height
        };

        stableFrames =
          0;

        requestAnimationFrame(
          check
        );

        return;
      }

      if (lastRect) {
        const stable =
          almostSame(
            rect.left,
            lastRect.left
          ) &&
          almostSame(
            rect.top,
            lastRect.top
          ) &&
          almostSame(
            rect.width,
            lastRect.width
          ) &&
          almostSame(
            rect.height,
            lastRect.height
          );

        if (stable) {
          stableFrames +=
            1;
        } else {
          stableFrames =
            0;
        }
      }

      lastRect = {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height
      };

      if (
        stableFrames <
        REQUIRED_STABLE_FRAMES
      ) {
        requestAnimationFrame(
          check
        );

        return;
      }

      /*
       * Noch einen Frame warten und dann an den neuesten
       * Artwork-Knoten neu binden.
       */
      requestAnimationFrame(
        () => {
          if (
            generation !==
            overlayState.galleryGeneration ||
            overlayState.galleryRepositioning
          ) {
            overlayState.galleryStableWatcherRunning =
              false;

            return;
          }

          const finalArtwork =
            getGalleryArtwork();

          if (
            !finalArtwork ||
            !finalArtwork.isConnected
          ) {
            overlayState.galleryStableWatcherRunning =
              false;

            return;
          }

          const gallery =
            document.querySelector(
              '.product-gallery'
            );

          if (!gallery) {
            overlayState.galleryStableWatcherRunning =
              false;

            return;
          }

          if (
            !overlayState.gallery
          ) {
            overlayState.gallery =
              document.createElement(
                'frame-overlay'
              );
          }

          /*
           * Galerie-Rahmen bewusst an document.body + position: fixed.
           * Dadurch benutzen Artwork (getBoundingClientRect) und Overlay
           * exakt dasselbe Koordinatensystem: den Viewport.
           * Das verhindert Drift durch sticky/transformierte Theme-Wrapper.
           */
          if (
            overlayState.gallery.parentElement !==
            document.body
          ) {
            document.body.appendChild(
              overlayState.gallery
            );
          }

          overlayState.gallery.style.zIndex = '2';

          overlayState.gallery.track(
            finalArtwork,
            true
          );

          overlayState.gallery.setOverlay(
            overlayState.src
          );

          overlayState.galleryWaitingForStable =
            false;

          overlayState.galleryStableWatcherRunning =
            false;

          overlayState.gallery.update();
        }
      );
    }

    requestAnimationFrame(
      check
    );
  }

  /* ------------------------------------------------------------------ *
   * POSTER-STABILITÄT PRÜFEN
   * ------------------------------------------------------------------ */

  function startModalStabilityWatcher() {
    if (
      overlayState.modalStableWatcherRunning
    ) {
      return;
    }

    overlayState.modalStableWatcherRunning =
      true;

    const generation =
      overlayState.modalGeneration;

    let lastArtwork =
      null;

    let lastRect =
      null;

    let stableFrames =
      0;

    const REQUIRED_STABLE_FRAMES =
      3;

    const TOLERANCE =
      0.5;

    function almostSame(a, b) {
      return (
        Math.abs(a - b) <= TOLERANCE
      );
    }

    function check() {
      if (
        generation !==
        overlayState.modalGeneration
      ) {
        overlayState.modalStableWatcherRunning =
          false;

        return;
      }

      if (
        overlayState.modalClosing ||
        overlayState.modalRepositioning
      ) {
        overlayState.modalStableWatcherRunning =
          false;

        return;
      }

      if (
        !overlayState.modalWaitingForStable
      ) {
        overlayState.modalStableWatcherRunning =
          false;

        return;
      }

      positionTeeinbluePreview();

      const artwork =
        getModalArtwork();

      if (
        !artwork ||
        !artwork.isConnected
      ) {
        lastArtwork =
          null;

        lastRect =
          null;

        stableFrames =
          0;

        requestAnimationFrame(
          check
        );

        return;
      }

      const rect =
        artwork.getBoundingClientRect();

      if (
        rect.width < 20 ||
        rect.height < 20
      ) {
        lastArtwork =
          artwork;

        lastRect =
          null;

        stableFrames =
          0;

        requestAnimationFrame(
          check
        );

        return;
      }

      if (
        artwork !== lastArtwork
      ) {
        lastArtwork =
          artwork;

        lastRect = {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height
        };

        stableFrames =
          0;

        requestAnimationFrame(
          check
        );

        return;
      }

      if (lastRect) {
        const stable =
          almostSame(
            rect.left,
            lastRect.left
          ) &&
          almostSame(
            rect.top,
            lastRect.top
          ) &&
          almostSame(
            rect.width,
            lastRect.width
          ) &&
          almostSame(
            rect.height,
            lastRect.height
          );

        if (stable) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
        }
      }

      lastRect = {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height
      };

      if (
        stableFrames <
        REQUIRED_STABLE_FRAMES
      ) {
        requestAnimationFrame(
          check
        );

        return;
      }

      /*
       * Auch wenn das Artwork schon stabil ist, den Modal-Rahmen erst
       * nach der festen 1-Sekunden-Verzögerung freigeben.
       */
      const revealWait =
        overlayState.modalRevealAt - Date.now();

      if (revealWait > 0) {
        setTimeout(
          () => requestAnimationFrame(check),
          revealWait
        );
        return;
      }

      requestAnimationFrame(
        () => {
          if (
            generation !==
            overlayState.modalGeneration
          ) {
            overlayState.modalStableWatcherRunning =
              false;

            return;
          }

          if (
            overlayState.modalClosing ||
            overlayState.modalRepositioning
          ) {
            overlayState.modalStableWatcherRunning =
              false;

            return;
          }

          const finalArtwork =
            getModalArtwork();

          if (
            !finalArtwork ||
            !finalArtwork.isConnected
          ) {
            overlayState.modalStableWatcherRunning =
              false;

            return;
          }

          overlayState.modalWaitingForStable =
            false;

          overlayState.modalStableWatcherRunning =
            false;

          if (!overlayState.modal) {
            overlayState.modal =
              document.createElement(
                'frame-overlay'
              );
          }

          if (
            overlayState.modal.parentElement !==
            document.body
          ) {
            document.body.appendChild(
              overlayState.modal
            );
          }

          overlayState.modal.track(
            finalArtwork,
            true
          );

          overlayState.modal.setOverlay(
            overlayState.src
          );

          overlayState.modal.update();
        }
      );
    }

    requestAnimationFrame(
      check
    );
  }

  /* ------------------------------------------------------------------ *
   * MODAL-ÖFFNUNG STARTEN
   * ------------------------------------------------------------------ */

  function beginModalOpening() {
    overlayState.modalGeneration +=
      1;

    // Rahmen im Modal bewusst ca. 0,5 Sekunden später anzeigen als die Vorschau.
    // Die Teeinblue-Vorschau kann sich in dieser Zeit fertig positionieren,
    // ohne dass der Rahmen die Zwischenbewegungen sichtbar mitmacht.
    overlayState.modalRevealAt =
      Date.now() + 500;

    overlayState.modalClosing =
      false;

    overlayState.modalRepositioning =
      false;

    overlayState.modalWaitingForStable =
      true;

    overlayState.modalStableWatcherRunning =
      false;

    hideModalFrame();

    startModalStabilityWatcher();
  }

  /* ------------------------------------------------------------------ *
   * MODAL SOFORT SCHLIESSEN
   * ------------------------------------------------------------------ */

  function hideModalFrameImmediately() {
    overlayState.modalGeneration +=
      1;

    overlayState.modalRevealAt =
      0;

    overlayState.modalClosing =
      true;

    overlayState.modalRepositioning =
      false;

    overlayState.modalWaitingForStable =
      false;

    overlayState.modalStableWatcherRunning =
      false;

    hideModalFrame();
  }

  /* ------------------------------------------------------------------ *
   * TEEINBLUE "NEU POSITIONIEREN"
   * ------------------------------------------------------------------ */

  function normalizedControlText(
    element
  ) {
    if (
      !(element instanceof Element)
    ) {
      return '';
    }

    /*
     * WICHTIG GEGEN FALSCH-ERKENNUNG ("Rahmen blitzt weg" beim Klick
     * auf einen beliebigen freien Bereich):
     *
     * Frueher fiel dieser Fallback auf "element" (das tatsaechlich
     * angeklickte Element) zurueck, wenn kein echtes Steuerelement
     * gefunden wurde. Danach wurde .textContent gelesen - das liefert
     * aber den GESAMTEN, verschachtelten Text ALLER Kind-Elemente.
     *
     * Liegt der Klick z.B. auf einem grossen, leeren Wrapper-Div (der
     * ganzen rechten Spalte neben dem Framechooser), landet darin
     * unter Umstaenden IRGENDWO tief verschachtelt auch ein von
     * Teeinblue verwaltetes "Neu positionieren"-Steuerelement (auch
     * wenn es visuell ganz woanders/unsichtbar sitzt). Der
     * Textvergleich weiter unten hat das faelschlich als Klick auf
     * genau dieses Steuerelement gewertet und sofort (ohne Gnadenfrist)
     * den Rahmen versteckt.
     *
     * Fix: OHNE echtes, konkretes Steuerelement (Button/Link/o.ae.)
     * gibt es KEINEN Text zum Vergleichen - kein Fallback auf
     * "element" mehr.
     */
    const clickable =
      element.closest(
        'button, [role="button"], a, [tabindex], [title], [aria-label], input[type="button"], input[type="submit"]'
      );

    if (!clickable) {
      return '';
    }

    const inputValue =
      clickable instanceof HTMLInputElement
        ? clickable.value
        : '';

    return (
      (
        clickable.textContent ||
        inputValue ||
        clickable.getAttribute(
          'aria-label'
        ) ||
        clickable.getAttribute(
          'title'
        ) ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
    );
  }

  function isTeeinblueRepositionControl(
    target
  ) {
    if (
      !(target instanceof Element)
    ) {
      return false;
    }

    const text =
      normalizedControlText(
        target
      );

    return (
      text.includes(
        'neu positionieren'
      ) ||
      text.includes(
        'neu-positionieren'
      ) ||
      text === 'positionieren' ||
      text === 'reposition' ||
      text.includes(
        'reposition image'
      )
    );
  }

  function isTeeinblueRepositionFinish(
    target
  ) {
    if (
      !(target instanceof Element)
    ) {
      return false;
    }

    const text =
      normalizedControlText(
        target
      );

    return (
      text === 'abbrechen' ||
      text === 'speichern' ||
      text === 'fertig' ||
      text === 'übernehmen' ||
      text === 'anwenden' ||
      text === 'cancel' ||
      text === 'save' ||
      text === 'done' ||
      text === 'apply'
    );
  }

  function beginRepositioning() {
    /*
     * WICHTIG:
     * Nur den Rahmen der normalen Produktvorschau verstecken.
     *
     * Der Rahmen IM "Neu positionieren"-Editor soll weiterhin
     * sichtbar sein.
     */
    overlayState.galleryGeneration +=
      1;

    overlayState.galleryRepositioning =
      true;

    overlayState.galleryWaitingForStable =
      false;

    overlayState.galleryStableWatcherRunning =
      false;

    hideGalleryFrame();

    overlayState.modalClosing =
      false;

    overlayState.modalRepositioning =
      false;
  }

  function finishRepositioning() {
    if (
      !overlayState.galleryRepositioning
    ) {
      return;
    }

    /*
     * Beim Klick auf Abbrechen/Speichern:
     * Editor-Rahmen sofort weg und Gallery weiter versteckt.
     */
    hideModalFrame();
    hideGalleryFrame();

    overlayState.modalGeneration +=
      1;

    overlayState.modalClosing =
      true;

    overlayState.modalRepositioning =
      false;

    overlayState.modalWaitingForStable =
      false;

    overlayState.modalStableWatcherRunning =
      false;

    overlayState.galleryGeneration +=
      1;

    overlayState.galleryRepositioning =
      false;

    overlayState.galleryWaitingForStable =
      true;

    overlayState.galleryStableWatcherRunning =
      false;

    startGalleryStabilityWatcher();

    [
      0,
      20,
      50,
      100,
      180,
      300,
      500,
      800
    ].forEach(
      (delay) => {
        setTimeout(
          () => {
            if (
              overlayState.galleryRepositioning
            ) {
              return;
            }

            positionTeeinbluePreview();
            scheduleOverlaySync();
          },
          delay
        );
      }
    );
  }

  document.addEventListener(
    'pointerdown',
    (event) => {
      const target =
        event.target;

      if (
        isTeeinblueRepositionControl(
          target
        )
      ) {
        beginRepositioning();
        return;
      }

      if (
        overlayState.galleryRepositioning &&
        isTeeinblueRepositionFinish(
          target
        )
      ) {
        finishRepositioning();
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target;

      if (
        isTeeinblueRepositionControl(
          target
        )
      ) {
        if (
          !overlayState.galleryRepositioning
        ) {
          beginRepositioning();
        }

        return;
      }

      if (
        overlayState.galleryRepositioning &&
        isTeeinblueRepositionFinish(
          target
        )
      ) {
        finishRepositioning();
      }
    },
    true
  );
    /* ------------------------------------------------------------------ *
   * ERKENNEN: VORSCHAU SCHLIESSEN
   * ------------------------------------------------------------------ */

  function isTeeinbluePreviewClose(target) {
    if (!(target instanceof Element)) {
      return false;
    }

    /*
     * Nicht vom noch vorhandenen Dialog-Wrapper abhängig machen.
     * Teeinblue kann beim Schließen Teile des Modals bereits im selben
     * Pointerdown umbauen. Der Rahmen muss trotzdem SOFORT verschwinden.
     */
    if (
      target.closest(
        '.tee-dialog-close'
      )
    ) {
      return true;
    }

    if (
      target.closest(
        '[title="Close"]'
      )
    ) {
      return true;
    }

    if (
      target.closest(
        '[aria-label="Close"]'
      )
    ) {
      return true;
    }

    const clickable =
      target.closest(
        'button, [role="button"], a'
      );

    if (!clickable) {
      return false;
    }

    const text =
      (
        clickable.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

    return (
      text.includes(
        'vorschau schließen'
      ) ||
      text.includes(
        'preview schließen'
      ) ||
      text === 'schließen'
    );
  }

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (
        !isTeeinbluePreviewClose(
          event.target
        )
      ) {
        return;
      }

      hideModalFrameImmediately();
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      if (
        !isTeeinbluePreviewClose(
          event.target
        )
      ) {
        return;
      }

      hideModalFrameImmediately();
    },
    true
  );

  /* ------------------------------------------------------------------ *
   * ERKENNEN: VORSCHAU ÖFFNEN
   * ------------------------------------------------------------------ */

  document.addEventListener(
    'pointerdown',
    (event) => {
      const target =
        event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const previewButton =
        target.closest(
          '.tee-btn--preview'
        );

      if (!previewButton) {
        return;
      }

      overlayState.modalClosing =
        false;

      overlayState.modalWaitingForStable =
        true;

      hideModalFrame();
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const previewButton =
        target.closest(
          '.tee-btn--preview'
        );

      if (!previewButton) {
        return;
      }

      beginModalOpening();

      [
        0,
        20,
        50,
        100,
        150,
        250,
        400,
        700
      ].forEach((delay) => {
        setTimeout(() => {
          positionTeeinbluePreview();
          scheduleOverlaySync();
        }, delay);
      });
    },
    true
  );

  /* ------------------------------------------------------------------ *
   * OVERLAYS SYNCHRONISIEREN
   * ------------------------------------------------------------------ */

  function syncOverlays() {
    syncMobilePreviewSpacing();
    positionTeeinbluePreview();

    const artworks =
      Array.from(
        document.querySelectorAll(
          'div.tee-printarea--artwork'
        )
      );

    const galleryArt =
      artworks.find(
        (el) =>
          !el.closest(
            '.vm--container'
          )
      ) ||
      null;

    const modalArt =
      artworks.find(
        (el) =>
          el.closest(
            '.vm--container'
          )
      ) ||
      null;

    /*
     * Kein Rahmen gewählt:
     * vorhandene Overlays sofort verstecken.
     */
    if (!overlayState.src) {
      if (overlayState.gallery) {
        overlayState.gallery.setOverlay('');
        overlayState.gallery.track(null, false);
        overlayState.gallery.hide();
      }

      if (overlayState.modal) {
        overlayState.modal.setOverlay('');
        overlayState.modal.track(null, true);
        overlayState.modal.hide();
      }

      return;
    }

    /* -------------------------------------------------------------- *
     * GALERIE-OVERLAY
     *
     * Sobald ein Rahmen gewählt ist, wird das Overlay IMMER an das
     * aktuell vorhandene Teeinblue-Artwork gebunden. Frühere
     * Reposition-/Stabilitäts-Flags dürfen die Sichtbarkeit nicht mehr
     * blockieren. Wenn Teeinblue den Artwork-Knoten austauscht, bindet
     * der nächste Sync automatisch an den neuen Knoten.
     * -------------------------------------------------------------- */

    const gallery =
      document.querySelector(
        '.product-gallery'
      );

    if (gallery && galleryArt) {
      if (!overlayState.gallery) {
        overlayState.gallery =
          document.createElement('frame-overlay');
      }

      /*
       * SCROLL-FIX V5 – RAHMEN DIREKT IM TEEINBLUE-ARTWORK:
       *
       * Der Rahmen wird nicht mehr per JavaScript gegen Viewport-Koordinaten
       * nachgeführt. Er sitzt direkt IM aktuell sichtbaren Artwork-Knoten.
       * Dadurch bekommt er exakt dieselben Sticky-/Transform-/Scroll-
       * Bewegungen wie Poster/Leinwand – ohne zeitlichen Versatz zwischen
       * Teeinblue-Compositor und requestAnimationFrame.
       *
       * Teeinblue darf den Artwork-Knoten weiterhin ersetzen. syncOverlays()
       * bzw. der laufende Tracker hängt denselben Overlay-Knoten sofort wieder
       * in das neu erzeugte Artwork ein.
       */
      const artPosition =
        window.getComputedStyle(galleryArt).position;

      if (artPosition === 'static') {
        galleryArt.style.setProperty(
          'position',
          'relative',
          'important'
        );
      }

      galleryArt.style.setProperty(
        'overflow',
        'visible',
        'important'
      );

      if (
        overlayState.gallery.parentElement !==
        galleryArt
      ) {
        galleryArt.appendChild(
          overlayState.gallery
        );
      }

      overlayState.gallery.setOverlay(
        overlayState.src
      );

      overlayState.gallery.track(
        galleryArt,
        false
      );

      overlayState.gallery.classList.remove(
        'frame-overlay--fixed'
      );

      overlayState.gallery.style.setProperty(
        'position',
        'absolute',
        'important'
      );
      overlayState.gallery.style.left =
        `${-OVERLAY_OUTSET * 100}%`;
      overlayState.gallery.style.top =
        `${-OVERLAY_OUTSET * 100}%`;
      overlayState.gallery.style.width =
        `${(1 + 2 * OVERLAY_OUTSET) * 100}%`;
      overlayState.gallery.style.height =
        `${(1 + 2 * OVERLAY_OUTSET) * 100}%`;
      overlayState.gallery.style.transform = 'none';
      overlayState.gallery.style.willChange = 'auto';
      overlayState.gallery.style.zIndex = '2';
      overlayState.gallery.style.setProperty(
        'display',
        'block',
        'important'
      );
    }

    /* -------------------------------------------------------------- *
     * MODAL-OVERLAY
     *
     * Gleiches Prinzip für die Teeinblue-Vorschau / das Modal:
     * Rahmen gewählt = Overlay auf dem aktuell vorhandenen Artwork.
     * -------------------------------------------------------------- */

    /*
     * WICHTIG BEIM SCHLIESSEN DER TEEINBLUE-VORSCHAU:
     *
     * Der Modal-DOM bleibt während der Schließanimation noch kurz bestehen.
     * Ohne diese Sperre würde der 400-ms-Sync das Overlay direkt wieder
     * einblenden und der Rahmen würde nach dem Schließen "nachhängen".
     *
     * Solange modalClosing = true gilt deshalb:
     * - Modal-Overlay IMMER verborgen halten
     * - erst wenn das Modal-Artwork wirklich aus dem DOM verschwunden ist,
     *   den Closing-State wieder freigeben
     */
    if (overlayState.modalClosing) {
      if (overlayState.modal) {
        overlayState.modal.track(
          null,
          true
        );

        overlayState.modal.hide();
      }

      if (!modalArt) {
        overlayState.modalClosing =
          false;

        overlayState.modalWasPresent =
          false;
      }
    } else if (modalArt) {
      overlayState.modalWasPresent =
        true;

      /*
       * Beim Öffnen der Teeinblue-Vorschau bleibt der Rahmen für 0,5 Sekunden
       * verborgen. Das verhindert das sichtbare Hin-und-her-Wackeln, während
       * Teeinblue das Modal noch positioniert bzw. skaliert.
       */
      const revealWait =
        overlayState.modalRevealAt - Date.now();

      if (revealWait > 0) {
        if (overlayState.modal) {
          overlayState.modal.track(
            null,
            true
          );

          overlayState.modal.hide();
        }

        setTimeout(
          scheduleOverlaySync,
          revealWait + 20
        );

        return;
      }

      if (!overlayState.modal) {
        overlayState.modal =
          document.createElement(
            'frame-overlay'
          );
      }

      if (
        overlayState.modal.parentElement !==
        document.body
      ) {
        document.body.appendChild(
          overlayState.modal
        );
      }

      overlayState.modal.setOverlay(
        overlayState.src
      );

      overlayState.modal.track(
        modalArt,
        true
      );

      overlayState.modal.update();
    } else {
      overlayState.modalWasPresent =
        false;

      if (overlayState.modal) {
        overlayState.modal.track(
          null,
          true
        );

        overlayState.modal.hide();
      }
    }
  }

  let overlayRaf = null;

  function scheduleOverlaySync() {
    if (overlayRaf) return;

    overlayRaf =
      requestAnimationFrame(() => {
        overlayRaf = null;

        syncOverlays();
      });
  }


  /*
   * Teeinblue ersetzt beim Upload, Rendern und Öffnen der Vorschau
   * teilweise den kompletten Artwork-Knoten. Nach einer Rahmenauswahl
   * deshalb mehrmals kurz hintereinander synchronisieren, damit das
   * Overlay ohne sichtbares "Verschwinden" wieder am neuesten Knoten
   * hängt.
   */
  function scheduleOverlayRefreshBurst() {
    [
      0,
      40,
      80,
      140,
      220,
      350,
      550,
      800,
      1200
    ].forEach((delay) => {
      setTimeout(
        scheduleOverlaySync,
        delay
      );
    });
  }

  /* ------------------------------------------------------------------ *
   * RAHMEN GEÄNDERT
   * ------------------------------------------------------------------ */

  document.addEventListener(
    'frame:change',
    (event) => {
      overlayState.src =
        event.detail.frame?.overlayUrl || '';

      syncOverlays();
      scheduleOverlayRefreshBurst();
      ensureOverlayTracking();
    }
  );

  /* ------------------------------------------------------------------ *
   * SCROLL
   *
   * ZURÜCKGESETZT (14.09.2026):
   * Ein Versuch, das Galerie-Overlay beim reinen Scrollen NICHT mehr neu
   * zu berechnen (in der Annahme, es würde sich als absolut positioniertes
   * Kind von .product-gallery ohnehin schon von selbst korrekt mitbewegen),
   * hat sich als falsch herausgestellt und die Anzeige beim Scrollen klar
   * verschlechtert (der Rahmen ist der Leinwand offenbar NICHT rein per
   * CSS gefolgt, sondern brauchte die laufende Neuberechnung). Deshalb
   * hier wieder der ursprüngliche, bewährte Stand: bei jedem Scroll-Event
   * ganz normal komplett neu synchronisieren (inkl. Galerie-Overlay).
   * ------------------------------------------------------------------ */

  /*
   * SCROLL-FIX V3:
   * Scroll-Events können zwischen zwei Layout-/Paint-Phasen eintreffen.
   * Genau dadurch konnte der Rahmen trotz fixed-Koordinaten noch wenige
   * Pixel nach oben/unten springen. Solange ein Rahmen aktiv ist, lesen wir
   * deshalb die endgültige Artwork-Position in JEDEM Animation-Frame aus.
   * Artwork und Overlay werden so im selben Render-Zyklus abgeglichen.
   */
  let overlayTrackingRaf = null;

  function trackOverlayEveryFrame() {
    overlayTrackingRaf = null;

    if (!overlayState.src) return;

    if (overlayState.gallery) {
      const currentGalleryArt = getGalleryArtwork();

      if (
        currentGalleryArt &&
        currentGalleryArt.isConnected
      ) {
        if (
          window.getComputedStyle(currentGalleryArt).position === 'static'
        ) {
          currentGalleryArt.style.setProperty(
            'position',
            'relative',
            'important'
          );
        }

        currentGalleryArt.style.setProperty(
          'overflow',
          'visible',
          'important'
        );

        if (
          overlayState.gallery.parentElement !== currentGalleryArt
        ) {
          currentGalleryArt.appendChild(overlayState.gallery);
        }

        overlayState.gallery.track(currentGalleryArt, false);
        overlayState.gallery.classList.remove('frame-overlay--fixed');
        overlayState.gallery.style.setProperty(
          'position',
          'absolute',
          'important'
        );
        overlayState.gallery.style.left = `${-OVERLAY_OUTSET * 100}%`;
        overlayState.gallery.style.top = `${-OVERLAY_OUTSET * 100}%`;
        overlayState.gallery.style.width = `${(1 + 2 * OVERLAY_OUTSET) * 100}%`;
        overlayState.gallery.style.height = `${(1 + 2 * OVERLAY_OUTSET) * 100}%`;
        overlayState.gallery.style.transform = 'none';
        overlayState.gallery.style.willChange = 'auto';
        overlayState.gallery.style.setProperty(
          'display',
          'block',
          'important'
        );
      }
    }

    if (
      overlayState.modal &&
      !overlayState.modalClosing
    ) {
      const currentModalArt = getModalArtwork();

      if (
        currentModalArt &&
        currentModalArt.isConnected &&
        overlayState.modal.__artwork !== currentModalArt
      ) {
        overlayState.modal.track(currentModalArt, true);
      }

      overlayState.modal.update();
    }

    overlayTrackingRaf =
      requestAnimationFrame(trackOverlayEveryFrame);
  }

  function ensureOverlayTracking() {
    if (
      overlayState.src &&
      !overlayTrackingRaf
    ) {
      overlayTrackingRaf =
        requestAnimationFrame(trackOverlayEveryFrame);
    }
  }

  window.addEventListener(
    'scroll',
    ensureOverlayTracking,
    {
      passive: true,
      capture: true
    }
  );

  if (window.visualViewport) {
    window.visualViewport.addEventListener(
      'scroll',
      ensureOverlayTracking,
      { passive: true }
    );

    window.visualViewport.addEventListener(
      'resize',
      ensureOverlayTracking,
      { passive: true }
    );
  }

  /* ------------------------------------------------------------------ *
   * RESIZE
   * ------------------------------------------------------------------ */

  window.addEventListener(
    'resize',
    () => {
      positionTeeinbluePreview();

      /*
       * Bei Größenänderung erneut stabilisieren.
       */
      if (
        getModalArtwork() &&
        !overlayState.modalClosing &&
        !overlayState.modalRepositioning
      ) {
        overlayState.modalGeneration +=
          1;

        overlayState.modalWaitingForStable =
          true;

        overlayState.modalStableWatcherRunning =
          false;

        hideModalFrame();

        startModalStabilityWatcher();
      }

      scheduleOverlaySync();
    }
  );

  /* ------------------------------------------------------------------ *
   * TEEINBLUE INJECTION
   * ------------------------------------------------------------------ */

  document.addEventListener(
    'teeinblue-event-component-injected',
    () => {
      positionTeeinbluePreview();

      if (
        overlayState.galleryRepositioning
      ) {
        hideGalleryFrame();
      }

      if (
        overlayState.galleryWaitingForStable &&
        !overlayState.galleryRepositioning
      ) {
        startGalleryStabilityWatcher();
      }

      scheduleOverlayRefreshBurst();
    }
  );

  /* ------------------------------------------------------------------ *
   * FALLBACK
   * ------------------------------------------------------------------ */

  setInterval(
    syncOverlays,
    400
  );

  /* ------------------------------------------------------------------ *
   * BUNDLE-ADD
   * ------------------------------------------------------------------ */

  function routesRoot() {
    return (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) || '/';
  }

  function newBundleId() {
    return (
      `${Date.now().toString(36)}-` +
      `${Math.random()
        .toString(36)
        .slice(2, 8)}`
    );
  }

  function showChooserError(
    chooser,
    message
  ) {
    let el =
      chooser.querySelector(
        '.frame-chooser__error'
      );

    if (!el) {
      el =
        document.createElement(
          'p'
        );

      el.className =
        'frame-chooser__error';

      el.setAttribute(
        'role',
        'alert'
      );

      chooser.appendChild(el);
    }

    el.textContent =
      message;
  }

  /* ------------------------------------------------------------------ *
   * KOMBINIERTE VARIANTE (mode === 'combined'): EIN Cart-Line-Item
   *
   * Statt eines zweiten cart/add.js-Calls wird die bereits im Formular
   * stehende Ziel-Variante (id) direkt durch die kombinierte Variante
   * (Größe + Rahmenfarbe, dasselbe Produkt) ersetzt. Zusätzlich werden
   * rein dekorative Properties gesetzt, damit der Warenkorb weiterhin
   * Rahmen-Label, Preisaufschlag und Bild wie gewohnt anzeigen kann, sowie
   * _Rahmen_Ohne_Variante_ID, damit der Kunde später im Warenkorb NUR den
   * Rahmen wieder entfernen kann (Variantenwechsel zurück auf "Ohne").
   *
   * Ist kein Rahmen gewählt ("Ohne"), wird nichts überschrieben: das
   * normale Variant-Picker-Formular zeigt dank des in variant-picker.liquid
   * fest auf "Ohne" gepinnten Rahmen-Werts bereits korrekt auf die
   * unframed Variante der aktuellen Größe.
   * ------------------------------------------------------------------ */

  function applyCombinedSelection(chooser, form) {
    const labelInput = chooser.querySelector(
      '[data-frame-prop-label]'
    );

    const priceInput = chooser.querySelector(
      '[data-frame-prop-price]'
    );

    const noneIdInput = chooser.querySelector(
      '[data-frame-prop-none-id]'
    );

    const frame = chooser.frameFor(chooser.selectedIndex);
    const variant = chooser.currentVariant();

    const idInput = form.querySelector('input[name="id"]');

    if (!frame || !variant) {
      [labelInput, priceInput, noneIdInput].forEach((input) => {
        if (!input) return;
        input.disabled = true;
        input.value = '';
      });

      /*
       * FIX: Ohne Rahmen. Das id-Feld wurde bei einem früheren Submit
       * (mit Rahmen) auf die kombinierte Rahmen-Variante überschrieben und
       * bleibt sonst so stehen, solange sich die Größe nicht ändert
       * (der Variant-Picker schreibt das Feld nur bei variant:change neu).
       * Deshalb hier explizit auf die "Ohne"-Variante der aktuellen Größe
       * zurücksetzen.
       */
      if (idInput) {
        const noneVariantId = chooser.noneVariantIdForSize(
          chooser.currentSize
        );

        if (noneVariantId) {
          idInput.value = noneVariantId;
        }
      }

      return;
    }

    if (idInput) {
      idInput.value = variant.id;
    }

    if (labelInput) {
      labelInput.disabled = false;
      labelInput.value = frame.label || '';
    }

    if (priceInput) {
      priceInput.disabled = false;
      priceInput.value = variant.price ? `+ ${variant.price}` : '';
    }

    if (noneIdInput) {
      if (variant.noneId) {
        noneIdInput.disabled = false;
        noneIdInput.value = variant.noneId;
      } else {
        noneIdInput.disabled = true;
        noneIdInput.value = '';
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * VOR DEM SUBMIT
   * ------------------------------------------------------------------ */

  document.addEventListener(
    'submit',
    (event) => {
      const chooser =
        document.querySelector(
          'frame-chooser'
        );

      const formId =
        event.target &&
        event.target.getAttribute &&
        event.target.getAttribute(
          'id'
        );

      if (
        !chooser ||
        !formId ||
        formId !== chooser.dataset.formId
      ) {
        return;
      }

      chooser
        .querySelector(
          '.frame-chooser__error'
        )
        ?.remove();

      if (chooser.mode === 'combined') {
        applyCombinedSelection(chooser, event.target);
        return;
      }

      const variant =
        chooser.currentVariant();

      const bundleInput =
        chooser.querySelector(
          '[data-frame-prop-bundle]'
        );

      if (
        !variant ||
        !bundleInput
      ) {
        return;
      }

      const cartType =
        window.themeVariables
          ?.settings
          ?.cartType ||
        'drawer';

      if (
        cartType === 'page'
      ) {
        const labelInput =
          chooser.querySelector(
            '[data-frame-prop-label]'
          );

        const roleInput =
          chooser.querySelector(
            '[data-frame-prop-role]'
          );

        [
          labelInput,
          bundleInput,
          roleInput
        ].forEach((input) => {
          if (input) {
            input.disabled =
              true;
          }
        });

        return;
      }

      const bundleId =
        newBundleId();

      bundleInput.value =
        bundleId;

      chooser.__pending = {
        bundleId,
        variantId:
          variant.id
      };

      window.__framePendingBundles[
        bundleId
      ] = true;
    },
    true
  );

  /* ------------------------------------------------------------------ *
   * KLEINES IMPACT-WARENKORB-POPUP BEI POSTER + RAHMEN
   * ------------------------------------------------------------------ */

  document.addEventListener(
    'variant:add',
    (event) => {
      const chooser =
        document.querySelector(
          'frame-chooser'
        );

      const pending =
        chooser &&
        chooser.__pending;

      if (
        !pending ||
        !event.detail
      ) {
        return;
      }

      const cartType =
        window.themeVariables
          ?.settings
          ?.cartType ||
        'drawer';

      if (
        cartType !== 'popover'
      ) {
        return;
      }

      event.detail.blockCartDrawerOpening =
        true;

      pending.showBundledPopover =
        true;
    },
    true
  );
    function showBundledPopover(
    sectionHtml
  ) {
    if (!sectionHtml) {
      return;
    }

    const drawer =
      document.querySelector(
        'cart-notification-drawer'
      );

    if (!drawer) {
      return;
    }

    const tempContent =
      document.createElement(
        'div'
      );

    tempContent.innerHTML =
      sectionHtml;

    const section =
      tempContent.querySelector(
        '.shopify-section'
      );

    if (!section) {
      return;
    }

    drawer.replaceChildren(
      ...section.children
    );

    if (
      typeof drawer.show ===
      'function'
    ) {
      drawer.show();
    }
  }

  /* ------------------------------------------------------------------ *
   * NACH ERFOLGREICHEM HAUPTPRODUKT-ADD:
   * ZUSATZPRODUKT / RAHMEN NACHSCHIEBEN
   * ------------------------------------------------------------------ */

  document.addEventListener(
    'variant:add',
    async (event) => {
      const chooser =
        document.querySelector(
          'frame-chooser'
        );

      const pending =
        chooser &&
        chooser.__pending;

      if (!pending) {
        return;
      }

      const posterItem =
        (
          event.detail?.items ||
          []
        ).find(
          (item) =>
            item.properties &&
            item.properties._bundle_id ===
              pending.bundleId
        );

      if (!posterItem) {
        return;
      }

      chooser.__pending =
        null;

      try {
        const response =
          await fetch(
            `${routesRoot()}cart/add.js`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json'
              },

              body: JSON.stringify({
                items: [
                  {
                    id:
                      pending.variantId,

                    quantity:
                      posterItem.quantity ||
                      1,

                    properties: {
                      _bundle_id:
                        pending.bundleId,

                      _bundle_role:
                        'rahmen'
                    }
                  }
                ],

                sections: [
                  'variant-added'
                ],

                sections_url:
                  `${routesRoot()}variants/${pending.variantId}`
              })
            }
          );

        const responseJson =
          await response.json();

        if (!response.ok) {
          throw new Error(
            `cart/add ${response.status}`
          );
        }

        /*
         * Wenn Impact als Cart-Popover läuft,
         * jetzt erst das gemeinsame Ergebnis
         * aus Poster + Rahmen anzeigen.
         */
        if (
          pending.showBundledPopover
        ) {
          showBundledPopover(
            responseJson
              ?.sections
              ?.['variant-added'] ||
            ''
          );
        }
      } catch (error) {
        /*
         * Falls das Hinzufügen des Rahmens
         * fehlschlägt, versuchen wir das bereits
         * hinzugefügte Poster mit derselben
         * Bundle-ID wieder zu entfernen.
         *
         * Dadurch bleibt möglichst kein
         * unvollständiges Bundle im Warenkorb.
         */
        try {
          const cartResponse =
            await fetch(
              `${routesRoot()}cart.js`
            );

          const cart =
            await cartResponse.json();

          const orphan =
            cart.items.find(
              (item) =>
                item.properties
                  ?._bundle_id ===
                pending.bundleId
            );

          if (orphan) {
            await fetch(
              `${routesRoot()}cart/change.js`,
              {
                method: 'POST',

                headers: {
                  'Content-Type':
                    'application/json'
                },

                body:
                  JSON.stringify({
                    id:
                      orphan.key,

                    quantity:
                      0
                  })
              }
            );
          }
        } catch (
          rollbackError
        ) {
          /*
           * Falls selbst der Rollback
           * fehlschlägt, übernimmt
           * frame-cart-sync.js später
           * das Aufräumen verbliebener
           * Bundle-Waisen.
           */
        }

        showChooserError(
          chooser,
          chooser.dataset
            .textAddError ||
            'Fehler beim Hinzufügen des Rahmens.'
        );
      } finally {
        delete window
          .__framePendingBundles[
            pending.bundleId
          ];

        /*
         * Impact / Cart Drawer aktualisieren.
         */
        document.dispatchEvent(
          new CustomEvent(
            'cart:refresh',
            {
              bubbles: true
            }
          )
        );
      }
    }
  );

})();